/**
 * 機能フラグ管理
 *
 * CATMSは全社が同じビルド・同じバックエンドを共有しているため、新機能は
 * 「まず1社だけON → 問題なければ全社へ」という手順で出す。その切り替えを行う画面。
 *
 * フラグも会社も増えるので、種類ごとの見出し・検索・絞り込みと、
 * 「機能ごと（どの会社でONか）」「会社ごと（この会社で何がONか）」の2つの見方を用意している。
 */
import { useEffect, useMemo, useState } from 'react';
import { Flag, Rocket, Search, ChevronDown, ChevronRight, Building2, LayoutList } from 'lucide-react';
import {
  getFeatureFlags, setCompanyFeatureFlag, rolloutFeatureFlag,
  type FeatureFlagDef,
} from '../services/api';

interface CompanyFlags { id: string; name: string; subdomain: string | null; flags: Record<string, boolean> }
type View = 'feature' | 'company';
type Filter = 'all' | 'partial' | 'allon' | 'alloff';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'すべて' }, { key: 'partial', label: '一部の会社でON' }, { key: 'allon', label: '全社ON' }, { key: 'alloff', label: '全社OFF' },
];
const LS_VIEW = 'ff_view', LS_OPEN = 'ff_open_cats';

export default function FeatureFlagsPage({ saRole }: { saRole?: string }) {
  const isOwner = saRole === 'owner';
  const [defined, setDefined] = useState<FeatureFlagDef[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [companies, setCompanies] = useState<CompanyFlags[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>(() => (localStorage.getItem(LS_VIEW) as View) || 'feature');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [companyId, setCompanyId] = useState('');
  const [openFlags, setOpenFlags] = useState<Set<string>>(new Set());
  const [closedCats, setClosedCats] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem(LS_OPEN) ?? '[]')); } catch { return new Set(); } });

  const load = async () => {
    setLoading(true);
    try {
      const d = await getFeatureFlags();
      setDefined(d.defined ?? []);
      setCategories(d.categories ?? []);
      setCompanies(d.companies ?? []);
      setCompanyId(prev => prev || d.companies?.[0]?.id || '');
    } catch (e: unknown) { setError(e instanceof Error ? e.message : '取得に失敗しました'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem(LS_VIEW, view); }, [view]);
  useEffect(() => { localStorage.setItem(LS_OPEN, JSON.stringify([...closedCats])); }, [closedCats]);

  const toggle = async (c: CompanyFlags, key: string, next: boolean) => {
    setBusy(`${c.id}:${key}`); setError('');
    try {
      const flags = await setCompanyFeatureFlag(c.id, key, next);
      setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, flags } : x));
    } catch (e: unknown) { setError(e instanceof Error ? e.message : '更新に失敗しました'); }
    finally { setBusy(null); }
  };

  const rollout = async (key: string, label: string) => {
    if (!window.confirm(`「${label}」を全社でONにします。\nシーステージ様など運用中の会社にも即座に反映されます。よろしいですか？`)) return;
    setBusy(`rollout:${key}`); setError('');
    try {
      const r = await rolloutFeatureFlag(key, true);
      alert(`${r.companies}社に展開しました。`);
      await load();
    } catch (e: unknown) { setError(e instanceof Error ? e.message : '展開に失敗しました'); }
    finally { setBusy(null); }
  };

  // ── 検索・絞り込み ──
  const onCount = (key: string) => companies.filter(c => c.flags[key] === true).length;
  const matchQ = (f: FeatureFlagDef) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [f.label, f.key, f.description, f.category ?? ''].some(t => t.toLowerCase().includes(s));
  };
  const matchFilter = (f: FeatureFlagDef) => {
    if (filter === 'all' || companies.length === 0) return true;
    const n = onCount(f.key);
    if (filter === 'allon') return n === companies.length;
    if (filter === 'alloff') return n === 0;
    return n > 0 && n < companies.length;
  };
  const visible = useMemo(() => defined.filter(f => matchQ(f) && matchFilter(f)), [defined, q, filter, companies]); // eslint-disable-line react-hooks/exhaustive-deps
  const catOrder = useMemo(() => {
    const base = categories.length ? categories : [...new Set(defined.map(f => f.category ?? 'その他'))];
    return base.filter(c => visible.some(f => (f.category ?? 'その他') === c));
  }, [categories, defined, visible]);
  const byCat = (cat: string) => visible.filter(f => (f.category ?? 'その他') === cat);
  const toggleCat = (cat: string) => setClosedCats(s => { const n = new Set(s); if (n.has(cat)) n.delete(cat); else n.add(cat); return n; });
  const toggleOpen = (key: string) => setOpenFlags(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const company = companies.find(c => c.id === companyId);

  const pill = (text: string, on: boolean) => (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: on ? '#dcfce7' : '#f1f5f9', color: on ? '#166534' : '#64748b', whiteSpace: 'nowrap' }}>{text}</span>
  );
  const toggleBtn = (c: CompanyFlags, key: string) => {
    const on = c.flags[key] === true;
    const k = `${c.id}:${key}`;
    return (
      <button onClick={() => toggle(c, key, !on)} disabled={busy === k}
        style={{ padding: '5px 14px', border: `1px solid ${on ? '#fecaca' : '#bfdbfe'}`, borderRadius: 6, background: '#fff', color: on ? '#dc2626' : '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, minWidth: 84 }}>
        {busy === k ? '...' : on ? 'OFFにする' : 'ONにする'}
      </button>
    );
  };

  return (
    <div style={{ maxWidth: 1040 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
        <Flag size={19} color="#0f172a" />
        <h2 style={{ fontSize: 19, fontWeight: 700, color: '#0f172a', margin: 0 }}>機能フラグ</h2>
        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 6 }}>{defined.length}項目 ・ {companies.length}社</span>
      </div>
      <p style={{ fontSize: 13, color: '#64748b', marginTop: 0, marginBottom: 16, lineHeight: 1.7 }}>
        新機能は会社ごとにON/OFFできます。まず検証したい会社だけONにして確認し、問題なければ「全社へ展開」で全社に広げます。切り替えは即時反映です（利用者は画面を開き直すと反映されます）。
      </p>

      {/* ツールバー: 見方の切替・検索・絞り込み */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14, position: 'sticky', top: 0, background: '#f8fafc', padding: '8px 0', zIndex: 5 }}>
        <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden' }}>
          {([['feature', '機能ごと', LayoutList], ['company', '会社ごと', Building2]] as const).map(([v, label, Icon]) => (
            <button key={v} onClick={() => setView(v)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', background: view === v ? '#0f172a' : '#fff', color: view === v ? '#fff' : '#475569', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              <Icon size={14} />{label}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: 10 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="機能名・キー・説明で検索（例: アルコール、push、乗下船）"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 30px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none' }} />
        </div>
        {view === 'feature' && (
          <div style={{ display: 'flex', gap: 4 }}>
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                style={{ padding: '6px 11px', borderRadius: 999, border: `1px solid ${filter === f.key ? '#0f172a' : '#cbd5e1'}`, background: filter === f.key ? '#0f172a' : '#fff', color: filter === f.key ? '#fff' : '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {f.label}
              </button>
            ))}
          </div>
        )}
        {view === 'company' && (
          <select value={companyId} onChange={e => setCompanyId(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, background: '#fff', minWidth: 220 }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}{c.subdomain ? `（${c.subdomain}）` : ''}</option>)}
          </select>
        )}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 13, padding: 24 }}>読み込み中...</div>
      ) : defined.length === 0 ? (
        <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: 28, textAlign: 'center', color: '#64748b', fontSize: 13, lineHeight: 1.8 }}>
          現在、切り替え可能な機能フラグはありません。
        </div>
      ) : visible.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, padding: 24 }}>該当する機能フラグはありません。</div>
      ) : view === 'feature' ? (
        /* ── 機能ごと: 種類 → フラグ（折りたたみ）→ 会社のON/OFF ── */
        catOrder.map(cat => {
          const flags = byCat(cat);
          const closed = closedCats.has(cat);
          return (
            <div key={cat} style={{ marginBottom: 14 }}>
              <button onClick={() => toggleCat(cat)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 'none', borderRadius: 8, background: '#e2e8f0', color: '#0f172a', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', textAlign: 'left' }}>
                {closed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                {cat}
                <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', marginLeft: 4 }}>{flags.length}項目</span>
              </button>
              {!closed && flags.map(f => {
                const n = onCount(f.key);
                const open = openFlags.has(f.key) || !!q.trim();
                const onNames = companies.filter(c => c.flags[f.key] === true).map(c => c.name);
                return (
                  <div key={f.key} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginTop: 8, overflow: 'hidden' }}>
                    <div onClick={() => toggleOpen(f.key)} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', cursor: 'pointer' }}>
                      <div style={{ paddingTop: 2, color: '#94a3b8' }}>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{f.label}</span>
                          {pill(n === companies.length && n > 0 ? '全社ON' : n === 0 ? '全社OFF' : `ON ${n}/${companies.length}社`, n > 0)}
                          {f.noRollout && pill('個別ONのみ', false)}
                        </div>
                        <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
                          <code style={{ color: '#94a3b8' }}>{f.key}</code>
                          {n > 0 && n < companies.length && <span style={{ marginLeft: 8 }}>ON: {onNames.join('・')}</span>}
                        </div>
                        {open && <div style={{ fontSize: 12, color: '#475569', marginTop: 8, lineHeight: 1.7 }}>{f.description}</div>}
                      </div>
                      {isOwner && !f.noRollout && n < companies.length && (
                        <button onClick={e => { e.stopPropagation(); rollout(f.key, f.label); }} disabled={busy === `rollout:${f.key}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: 'none', borderRadius: 8, background: '#0f172a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                          <Rocket size={13} />全社へ展開
                        </button>
                      )}
                    </div>
                    {open && (
                      <div style={{ borderTop: '1px solid #f1f5f9', padding: '10px 14px 12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 6 }}>
                        {companies.map(c => {
                          const on = c.flags[f.key] === true;
                          return (
                            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: on ? '#f0fdf4' : '#f8fafc', border: `1px solid ${on ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 8 }}>
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{c.name}</span>
                                {c.subdomain && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>{c.subdomain}</span>}
                              </div>
                              {pill(on ? 'ON' : 'OFF', on)}
                              {toggleBtn(c, f.key)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })
      ) : (
        /* ── 会社ごと: 1社を選び、種類ごとにON/OFF ── */
        !company ? <div style={{ color: '#94a3b8', fontSize: 13, padding: 24 }}>会社を選んでください。</div> : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{company.name}</span>
              {pill(`ON ${defined.filter(f => company.flags[f.key] === true).length}/${defined.length}項目`, true)}
            </div>
            {catOrder.map(cat => {
              const flags = byCat(cat);
              const closed = closedCats.has(cat);
              const onN = flags.filter(f => company.flags[f.key] === true).length;
              return (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <button onClick={() => toggleCat(cat)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 'none', borderRadius: 8, background: '#e2e8f0', color: '#0f172a', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', textAlign: 'left' }}>
                    {closed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                    {cat}
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b', marginLeft: 4 }}>ON {onN}/{flags.length}</span>
                  </button>
                  {!closed && flags.map(f => {
                    const on = company.flags[f.key] === true;
                    const others = companies.filter(c => c.id !== company.id && c.flags[f.key] === true).length;
                    return (
                      <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginTop: 6, background: on ? '#f0fdf4' : '#fff', border: `1px solid ${on ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{f.label}</div>
                          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                            <code style={{ color: '#94a3b8' }}>{f.key}</code>
                            <span style={{ marginLeft: 8 }}>他社でON: {others}/{companies.length - 1}社</span>
                          </div>
                          {q.trim() && <div style={{ fontSize: 12, color: '#475569', marginTop: 6, lineHeight: 1.6 }}>{f.description}</div>}
                        </div>
                        {pill(on ? 'ON' : 'OFF', on)}
                        {toggleBtn(company, f.key)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )
      )}
    </div>
  );
}
